import { makeStyles } from '@material-ui/core';
import { noop } from 'Helpers/PropTypes';
import React from 'react';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import {
  Box, Stack,
} from '@facet-wealth/design';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import CheckCircleIcon from '@mui/icons-material/esm/CheckCircle';
import InfoIcon from '@mui/icons-material/esm/Info';
import WarningIcon from '@mui/icons-material/esm/Warning';
import ErrorIcon from '@mui/icons-material/esm/Error';
import HelpIcon from '@mui/icons-material/esm/Help';
import Tooltip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Tooltip';
import classNames from 'classnames';
import PropTypes from 'prop-types';


function getStatusIcon(status) {
  switch (status) {
    case 'SUCCESS':
    case 'COMPLETE':
      return CheckCircleIcon;
    case 'WARN':
      return WarningIcon;
    case 'ERROR':
      return ErrorIcon;
    case 'INFO':
      return InfoIcon;
    case 'INCOMPLETE':
      return HelpIcon;
    default:
      return undefined;
  }
}


const useStyles = makeStyles(theme => ({
  containerElement: {
    padding: theme.spacing(1, 2),
    cursor: props => (props.onClick ? 'pointer' : 'default'),
    width: '100%',
    border: '1px solid #EEEEEE',
    borderRadius: theme.radius.md,
    background: props => (props.status === 'DISABLED' ? '#EEEEEE' : theme.palette.common.white),
  },
  status: {
    color: props => {
      switch (props.status) {
        case 'SUCCESS':
        case 'COMPLETE':
          return theme.palette.success.main;
        case 'WARN':
          return theme.palette.facet.orange;
        case 'ERROR':
          return theme.palette.error.main;
        case 'INFO':
          return theme.palette.facet.primaryBlue;
        case 'INCOMPLETE':
          return theme.palette.facet.orange;
        case 'DISABLED':
          return theme.palette.facet.accentGray;
        default:
          return undefined;
      }
    },
  },
  leftMedia: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    width: 24,
    flexShrink: 0,
  },
  image: {
    maxWidth: 24,
    maxHeight: 24,
    width: '100%',
  },
  loading: {
    padding: 0,
  },
  actionGroup: {
    marginLeft: theme.spacing(2),
    flexShrink: 0,
  },
}));


function ItemCard({
  className,
  title,
  titleTooltip,
  subtitle,
  subtitleExtra,
  imageURL,
  loading,
  status,
  statusText,
  actionGroup,
  onClick,
  test,
}) {
  const classes = useStyles({ status, onClick });
  const showImage = !loading && imageURL;
  const showLeftMedia = loading || showImage;

  const StatusIcon = getStatusIcon(status);

  const renderTitle = () => {
    let titleComponent = title && <Text variant={'h5'} weight={'bold'} align={'left'} color={'textPrimary'} noWrap value={title} />;

    if (titleComponent && titleTooltip) {
      titleComponent = (
        <Tooltip
          placement={'top'}
          title={'titleTooltip'}
        >
          {titleComponent}
        </Tooltip>
      );
    }

    return titleComponent;
  };

  return (
    <div className={classNames(classes.containerElement, className)} onClick={onClick} onKeyDown={noop} role="button" tabIndex={0} test={test}>
      <Stack alignItems={'center'} spacing={2} justifyContent="flex-start">
        {showLeftMedia && (
          <div className={classes.leftMedia}>
            {loading && <Box width={24}><LoadingIndicator className={classes.loading} size={24} /></Box>}
            {showImage && <img className={classes.image} src={imageURL} alt={'account'} />}
          </div>
        )}
        <Box overflow={'hidden'} width={'100%'}>
          <Stack direction={'column'} spacing={1} alignItems="flex-start" justifyContent="flex-start">
            {(title || subtitle) && (
              <Box overflow={'hidden'} width={'100%'}>
                {renderTitle()}
                {subtitle && (
                <Stack direction={'row'} justifyContent={'space-between'} spacing={1} alignItems="flex-start">
                  <Text align={'left'} color={'textSecondary'} weight={'bold'} value={subtitle} noWrap />
                  {subtitleExtra && <Text align={'right'} color={'textSecondary'} value={subtitleExtra} noWrap />}
                </Stack>)}
              </Box>
            )}
            {StatusIcon && (
              <Box width={'100%'}>
                <Stack spacing={1} alignItems={'center'} justifyContent="flex-start">
                  <StatusIcon className={classes.status} />
                  <Text align={'left'} color={'textSecondary'} weight={'bold'} noWrap value={statusText} />
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
        <div style={{ marginLeft: 'auto' }} />
        {actionGroup && <div style={{ flexShrink: 0 }}>{actionGroup}</div>}
      </Stack>
    </div>
  );
}

ItemCard.defaultProps = {
  className: '',
  title: null,
  titleTooltip: null,
  subtitle: null,
  subtitleExtra: null,
  imageURL: null,
  loading: false,
  status: null,
  statusText: null,
  actionGroup: null,
  onClick: null,
  test: null,
};

ItemCard.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  titleTooltip: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleExtra: PropTypes.string,
  imageURL: PropTypes.string,
  loading: PropTypes.bool,
  status: PropTypes.oneOf(['SUCCESS', 'COMPLETE', 'WARN', 'ERROR', 'INFO', 'INCOMPLETE', 'DISABLED']),
  statusText: PropTypes.string,
  actionGroup: PropTypes.node,
  onClick: PropTypes.func,
  test: PropTypes.string,
};

export default ItemCard;
